import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Trophy, RotateCcw, Star } from 'lucide-react';

// Session-only leaderboard for the current classroom. Points are read straight
// from room.session_scores (populated by the backend on every awarded exercise —
// standard puzzles AND Capture Activities). No polling of its own: the parent
// already refetches the room every 2s, so this panel updates in real time on
// re-renders. Ties keep the same score; visual tiebreak is participant name
// (deterministic, does not modify actual points).
export default function LeaderboardPanel({ room, isTeacher, onReset }) {
  const rows = useMemo(() => {
    if (!room) return [];
    const scores = room.session_scores || {};
    const list = (room.participants || [])
      .filter(p => p.role !== 'teacher')
      .map(p => ({
        user_id: p.user_id,
        name: p.name,
        points: Number(scores[p.user_id] || 0),
      }));
    list.sort((a, b) => b.points - a.points || a.name.localeCompare(b.name));
    return list;
  }, [room]);

  const podium = rows.slice(0, 3);
  const rest = rows.slice(3);

  const medalFor = (idx) => (idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉');
  const podiumTone = (idx) => (
    idx === 0
      ? 'from-amber-400 to-yellow-300 border-amber-400'
      : idx === 1
        ? 'from-slate-300 to-slate-200 border-slate-300'
        : 'from-orange-400 to-orange-300 border-orange-400'
  );

  if (rows.length === 0) {
    return (
      <div className="text-sm text-slate-500 text-center py-12" data-testid="leaderboard-empty">
        <Trophy className="w-8 h-8 mx-auto mb-2 text-slate-400"/>
        Aún no hay alumnos en esta clase.
      </div>
    );
  }

  return (
    <div data-testid="session-leaderboard" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display font-extrabold text-lg flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500"/> Leaderboard de la clase
          </div>
          <div className="text-xs text-slate-500">Puntos acumulados en la sesión actual</div>
        </div>
        {isTeacher && (
          <Button
            data-testid="reset-session-btn"
            size="sm" variant="outline"
            className="rounded-full border-rose-300 text-rose-600 hover:bg-rose-50"
            onClick={onReset}>
            <RotateCcw className="w-3.5 h-3.5 mr-1"/> Nueva sesión
          </Button>
        )}
      </div>

      {/* Podium (up to top 3) */}
      <div className="grid gap-2" data-testid="leaderboard-podium">
        {podium.map((r, i) => (
          <div
            key={r.user_id}
            data-testid={`lb-podium-${i+1}`}
            className={`flex items-center justify-between rounded-2xl border-2 bg-gradient-to-r ${podiumTone(i)} px-4 py-3 shadow-sm`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="text-3xl leading-none select-none" aria-hidden="true">{medalFor(i)}</div>
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-700">
                  {i === 0 ? '1er lugar' : i === 1 ? '2do lugar' : '3er lugar'}
                </div>
                <div className="font-display font-extrabold text-lg text-slate-900 truncate">{r.name}</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 font-display font-extrabold text-2xl text-slate-900">
              <Star className="w-5 h-5 text-amber-600 fill-amber-500"/> {r.points}
              <span className="text-xs font-semibold text-slate-700 ml-1">pts</span>
            </div>
          </div>
        ))}
      </div>

      {/* Remaining participants */}
      {rest.length > 0 && (
        <div className="space-y-1.5" data-testid="leaderboard-rest">
          {rest.map((r, idx) => (
            <div
              key={r.user_id}
              data-testid={`lb-rest-${idx + 4}`}
              className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-7 h-7 rounded-full bg-slate-100 grid place-items-center text-xs font-bold text-slate-600">
                  {idx + 4}
                </div>
                <div className="font-semibold truncate">{r.name}</div>
              </div>
              <div className="flex items-center gap-1 font-mono font-bold text-slate-800">
                <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400"/> {r.points}
                <span className="text-xs text-slate-500">pts</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
